import Link from 'next/link';
import { Box } from 'theme-ui';
import { useEffect, useRef, useState } from 'react';
import styles from '@features/cms/components/collections/ProductPremiumBlock/components/ProductPremiumDescription/ProductPremiumDescription.styles';
import ProductPremiumImage from '@features/cms/components/collections/ProductPremiumBlock/components/ProductPremiumImage/ProductPremiumImage';
import { IProductPremiumDescriptionVariantBlock, IProductPremiumSingleBlock, TProductPremiumSingleBlockWitParsedImage } from '@features/cms/typescript';
import useProductPremiumSlide from '@hooks/useProductPremiumSlide';
import Heading from '@components/Heading/Heading';
import sanitizeHtml from '@utils/sanitizeHtml';
import { IParsedImage } from '@components/typescript';
import Button from '@components/Button/Button';
import useResponsive from '@features/responsive/hooks/useResponsive';
import scrollToElementById from '@utils/scrollToElementById';
import stackedStyles from '@features/cms/components/collections/ProductPremiumBlock/ProductPremiumStacked.styles';
type PropsType = {
  blocks: TProductPremiumSingleBlockWitParsedImage[];
  activeBlock: number;
  viewportHeight: number;
  viewportWidth: number;
  isFirst: boolean;
  isStackedMobile?: boolean;
};
const ProductPremiumDescription = ({
  blocks,
  activeBlock,
  viewportHeight,
  viewportWidth,
  isFirst,
  isStackedMobile = false
}: PropsType) => {
  const slideContainerRef = useRef<HTMLDivElement>(null);
  const mainImageConatinerRef = useRef<HTMLDivElement>(null);
  const [desktopContentHeight, setDesktopContentHeight] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const [imageHeight, setImageHeight] = useState(0);
  const {
    isMobile: isMobileSmall,
    isMobilelg,
    isTablet: isTabletSmall,
    isTabletlg
  } = useResponsive();
  const isMobile = isMobileSmall || isMobilelg;
  const isTablet = isTabletSmall || isTabletlg;
  const {
    previousBlock,
    currentBlock
  } = useProductPremiumSlide(blocks, activeBlock);
  const [blockData, setBlockData] = useState<IProductPremiumSingleBlock<IProductPremiumDescriptionVariantBlock, IParsedImage>>(currentBlock as IProductPremiumSingleBlock<IProductPremiumDescriptionVariantBlock, IParsedImage>);
  const block = currentBlock as IProductPremiumSingleBlock<IProductPremiumDescriptionVariantBlock, IParsedImage>;
  const recalculateImageHeight = () => mainImageConatinerRef.current?.clientHeight || 0;
  const recalculateDesktopImageHeight = () => {
    if (slideContainerRef.current === null || mainImageConatinerRef.current === null) return;
    mainImageConatinerRef.current.style.top = 'unset';
    mainImageConatinerRef.current.style.bottom = 'unset';
    slideContainerRef.current.style.top = 'unset';
    const mainImageWidth = mainImageConatinerRef.current.clientWidth;
    const mainImageHeightCalc = mainImageWidth / 4 * 3;
    mainImageConatinerRef.current.style.height = `${mainImageHeightCalc}px`;
    slideContainerRef.current.style.height = `${mainImageHeightCalc}px`;
  };
  useEffect(() => {
    if (!isMobile && !isTablet) {
      setTimeout(() => {
        if (contentRef.current === null) return;
        const {
          clientHeight
        } = contentRef.current.firstElementChild as HTMLDivElement;
        setDesktopContentHeight(clientHeight);
      }, 0);
    }
    const timeoutId = window.setTimeout(() => {
      const nextHeight = recalculateImageHeight();
      setImageHeight(nextHeight > 0 ? nextHeight : viewportWidth / 4 * 3);
    }, 50);
    return () => window.clearTimeout(timeoutId);
  }, [blockData, viewportHeight, viewportWidth]);
  useEffect(() => {
    if (isStackedMobile) return;
    if (contentRef.current === null || slideContainerRef.current === null || mainImageConatinerRef.current === null) return;
    if (isTablet || isMobile) {
      mainImageConatinerRef.current.style.opacity = '0';
      const topMargin = isMobile ? 128 : 160;
      const slideMargin = isMobile ? 64 : 80;
      const calcMainImageConatinerHeight = viewportHeight - topMargin - slideMargin - contentRef.current.clientHeight;
      mainImageConatinerRef.current.style.height = `${Math.max(calcMainImageConatinerHeight, 0)}px`;
      slideContainerRef.current.style.height = `auto`;
      if (activeBlock % 2 === 0) {
        setTimeout(() => {
          if (slideContainerRef.current === null || mainImageConatinerRef.current === null) return;
          slideContainerRef.current.style.top = isMobile ? '64px' : '80px';
          mainImageConatinerRef.current.style.top = 'unset';
          mainImageConatinerRef.current.style.bottom = isMobile ? '64px' : '80px';
          setTimeout(() => {
            if (slideContainerRef.current === null || mainImageConatinerRef.current === null) return;
            mainImageConatinerRef.current.style.opacity = '1';
            if (contentRef.current) contentRef.current.style.opacity = '1';
          }, 400);
        }, 400);
      } else {
        setTimeout(() => {
          if (slideContainerRef.current === null || mainImageConatinerRef.current === null) return;
          const height = slideContainerRef.current.clientHeight;
          slideContainerRef.current.style.top = `calc(100% - ${isMobile ? '64' : '80'}px - ${height}px)`;
          mainImageConatinerRef.current.style.top = isMobile ? '64px' : '80px';
          mainImageConatinerRef.current.style.bottom = 'unset';
          setTimeout(() => {
            if (slideContainerRef.current === null || mainImageConatinerRef.current === null) return;
            mainImageConatinerRef.current.style.opacity = '1';
            if (contentRef.current) contentRef.current.style.opacity = '1';
          }, 400);
        }, 400);
      }
      setTimeout(() => {
        setBlockData({
          ...block
        });
        if (contentRef.current) contentRef.current.style.opacity = '1';
      }, 600);
    }
    if (!isMobile && !isTablet) {
      recalculateDesktopImageHeight();
      const desktopHeight = mainImageConatinerRef.current.clientHeight || viewportWidth / 4 * 3;
      setImageHeight(desktopHeight);
      contentRef.current.style.opacity = '0.5';
      setTimeout(() => {
        setBlockData({
          ...block
        });
        if (contentRef.current) contentRef.current.style.opacity = '1';
      }, 200);
    }
  }, [currentBlock, viewportHeight, viewportWidth, isMobile, isTablet, isStackedMobile]);
  if (isStackedMobile) {
    const stackedBlock = blocks[0] as IProductPremiumSingleBlock<IProductPremiumDescriptionVariantBlock, IParsedImage>;
    return <div sx={stackedStyles.slide}>
        <div sx={stackedStyles.mainImageDescription}>
          <ProductPremiumImage image={stackedBlock.image} video={stackedBlock.video} fullScreen />
        </div>
        <div sx={stackedStyles.contentDescription}>
          <Heading h={isFirst ? 1 : 2} headingType={2} mobile>
            <span dangerouslySetInnerHTML={sanitizeHtml(stackedBlock.variant.descriptionTitle)} />
          </Heading>
          <p className="big" sx={styles.descriptionVariantText}>
            {stackedBlock.variant.descriptionText}
          </p>
          {stackedBlock.variant.descriptionButtonUrl && stackedBlock.variant.descriptionButtonText && <>
              {stackedBlock.variant.descriptionButtonUrl.slice(0, 1) === '#' ? <Button onClick={() => scrollToElementById(stackedBlock.variant.descriptionButtonUrl.substring(1))} variantColor="dark" size="small" letterCase="lowercase">
                  {stackedBlock.variant.descriptionButtonText}
                </Button> : <Link href={stackedBlock.variant.descriptionButtonUrl}>
                  <Button variantColor="dark" size="small" letterCase="lowercase">
                    {stackedBlock.variant.descriptionButtonText}
                  </Button>
                </Link>}
            </>}
        </div>
      </div>;
  }
  return <div sx={styles.sliderGrid}>
      <Box sx={styles.descriptionVariantContainer} ref={slideContainerRef}>
        <div sx={styles.descriptionVariantWrapper(previousBlock.position, currentBlock.position)}>
          <div sx={styles.contentContainer(desktopContentHeight)} ref={contentRef}>
            <div>
              <Heading h={isFirst ? 1 : 2} headingType={2} mobile>
                <span dangerouslySetInnerHTML={sanitizeHtml(blockData.variant.descriptionTitle)} />
              </Heading>
              <p className="big" sx={styles.descriptionVariantText}>
                {blockData.variant.descriptionText}
              </p>
              {blockData.variant.descriptionButtonUrl && blockData.variant.descriptionButtonText && <>
                  {blockData.variant.descriptionButtonUrl.slice(0, 1) === '#' ? <Button onClick={() => scrollToElementById(blockData.variant.descriptionButtonUrl.substring(1))} variantColor="dark" size="small" letterCase="lowercase">
                      {blockData.variant.descriptionButtonText}
                    </Button> : <Link href={blockData.variant.descriptionButtonUrl}>
                      <Button variantColor="dark" size="small" letterCase="lowercase">
                        {blockData.variant.descriptionButtonText}
                      </Button>
                    </Link>}
                </>}
            </div>
          </div>
        </div>
      </Box>
      <div sx={styles.mainImageContainer} ref={mainImageConatinerRef}>
        <ProductPremiumImage image={blockData.image} video={blockData.video} height={imageHeight} />
      </div>
    </div>;
};
export default ProductPremiumDescription;